import { ALLOWED_IMAGE_TYPES, ALLOWED_IMAGE_EXTENSIONS, MAX_IMAGE_SIZE } from '../constants/app';

export interface ImageValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validates an image file for type and size
 * @param file - The file to validate
 * @returns Validation result with error message if invalid
 */
export function validateImageFile(file: File): ImageValidationResult {
  // Check file type
  const isValidType = ALLOWED_IMAGE_TYPES.includes(
    file.type as (typeof ALLOWED_IMAGE_TYPES)[number]
  );

  if (!isValidType) {
    // Check if it's an SVG (common mistake)
    if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
      return {
        valid: false,
        error: 'SVG files are not supported. Please use JPG, PNG, GIF, or WebP.',
      };
    }

    const allowedExtensions = ALLOWED_IMAGE_EXTENSIONS.join(', ').toUpperCase();
    return {
      valid: false,
      error: `Invalid file type. Please use ${allowedExtensions} format.`,
    };
  }

  // Check file size
  if (file.size > MAX_IMAGE_SIZE) {
    const maxSizeMB = MAX_IMAGE_SIZE / (1024 * 1024);
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File is too large (${fileSizeMB}MB). Maximum size is ${maxSizeMB}MB.`,
    };
  }

  return { valid: true };
}

/**
 * Formats the allowed image types for display in UI
 * @returns Human-readable string of allowed formats
 */
export function getAllowedImageFormatsDisplay(): string {
  return ALLOWED_IMAGE_EXTENSIONS.map(ext => ext.toUpperCase()).join(', ');
}
